import type { GetAuthUsersResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetAuthUsersQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAuthUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

const mapTrpcUserToGraphQL = (
  user: GetAuthUsersResponseRow
): StrictMappedGraphQLSelection<GetAuthUsersQuery['auth_user'][number]> => ({
  ...user,
  customRoles: (user.customRoles ?? [])
    .filter((customRole) => customRole.customRole != null)
    .map((customRole) => ({ ...customRole, role: customRole.customRole! })),
  userGroupUsers: (user.userGroupUsers ?? [])
    .filter((userGroupUser) => userGroupUser.userGroups != null)
    .map((userGroupUser) => ({
      ...userGroupUser,
      userGroups: userGroupUser.userGroups!,
    })),
});

const mapTrpcUsersToGraphQL = (
  trpcData: GetAuthUsersResponseRow[]
): StrictMappedGraphQLSelection<GetAuthUsersQuery> => ({
  auth_user: trpcData.map(mapTrpcUserToGraphQL),
  auth_user_aggregate: { aggregate: { count: trpcData.length } },
});

type UseGetAuthUsersArgs = Record<string, never>;

/**
 * The `where` for the GraphQL branch, hoisted out of the hook so
 * `useGetAuthUsers.test.ts` can pin it. `graphqlVariables` is typed
 * `Record<string, unknown>` (`hook-factories/types.ts`), so TypeScript checks
 * nothing about this shape — the test is the only mechanical guard.
 */
export const getAuthUsersWhere = {
  /*
   * `organisationusers: {}` is redundant on paper and load-bearing in
   * practice (COI-1043) — do not remove it as dead weight.
   *
   * It cannot change the result set: both select permissions on `auth.user`
   * already filter to `organisationusers.OrgKey = X-Hasura-org-id`, so every
   * row that survives the permission filter has a matching membership row by
   * construction.
   *
   * What it changes is the plan. The admin roles that open this page
   * (`ReadOnly`, and `RiskManager` / `CustomerSupport` which nest it) inherit
   * BOTH `app-user` and `read:auth-user`, so Hasura ORs their two identical
   * row filters into `EXISTS (...) OR EXISTS (...)`. Postgres cannot turn an
   * OR of EXISTS into a semi-join, so it sequentially scans the whole of
   * `auth.user` — every user of every org, on MultiTenant — evaluating
   * hashed subplans per row. This one extra predicate is emitted as a single
   * bare EXISTS, which the planner does convert to a semi-join over
   * `idx_organisationUser_userid_orgkey`, narrowing to the session org before
   * the OR filter runs.
   *
   * Measured against a Hasura v2.50.1 + Postgres 16.11 worktree stack with
   * 5,025 global users and 25 in the requesting org: 1367ms -> 8ms, with
   * identical results (24 rows).
   *
   * Filtering on the org-scoped `organisationusers.RoleKey` instead would
   * also fix the plan but is NOT equivalent — `auth.user.RoleKey` is frozen
   * per COI-824 and the two columns disagree, so it returns different rows.
   */
  _and: [
    { organisationusers: {} },
    {
      _or: [
        { RoleKey: { _neq: 'ThirdPartyRespondent' } },
        { RoleKey: { _is_null: true } },
      ],
    },
  ],
};

/**
 * `frontend.user.users` is gated on `settings_users:read` and a denial reaches
 * this hook as FORBIDDEN, which `useErrorNotification` surfaces as a toast —
 * the V2 behaviour this restores (COI-774).
 *
 * `retry: false` is part of that parity, not a tuning choice. react-query's
 * default re-fetches a failed query three more times, so one settled denial
 * would become four requests and four server-side warns; Apollo issued one.
 * Sentry reporting is deliberately left ON: V2's `graphqlErrorHandler` routed
 * these to Sentry too, so silencing them here would be a new divergence.
 */
export const useGetAuthUsers = createQueryHook<
  UseGetAuthUsersArgs,
  GetAuthUsersResponseRow[],
  GetAuthUsersQuery
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.user.users.queryOptions(undefined, { retry: false }),
  mapTrpcDataToGraphQL: mapTrpcUsersToGraphQL,
  graphqlDocument: GetAuthUsersDocument,
  graphqlVariables: () => ({ where: getAuthUsersWhere }),
});
